import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { Button, Rating, Badge } from "@shop/ui";
import { formatPrice, relativeTime, formatDateTime } from "@shop/utils";
import { fetchProducts, fetchReviews, type Review } from "../api.ts";
import { recommend } from "../recommend.ts";
import { useCart } from "../store.ts";
import type { Product as P } from "../data/products.ts";
import { Pic } from "../components/Pic.tsx";

export default function Product() {
  const { slug } = useParams();
  const [all, setAll] = useState<P[] | null>(null);
  const [reviews, setReviews] = useState<Review[] | null>(null);
  const [reco, setReco] = useState<P[]>([]);
  const cart = useCart();

  useEffect(() => {
    fetchProducts().then(setAll);
    if (slug) fetchReviews(slug).then(setReviews);
  }, [slug]);

  const product = all?.find((p) => p.slug === slug);
  if (!all) return <p className="empty">加载中…</p>;
  if (!product) return <p className="empty">没有这个商品</p>;

  const addToCart = () => {
    cart.add(product);
    window.analytics?.track("add_to_cart", { id: product.id });
    // 加购之后立刻算「猜你喜欢」,同步跑在点击事件里
    setReco(recommend([...cart.items.map((i) => i.product), product], all));
  };

  return (
    <>
      <div className="product">
        <Pic name={product.slug} alt={product.name} sizes="(max-width: 720px) 100vw, 560px" fetchPriority="high" />
        <div className="meta">
          <h1>{product.name}</h1>
          <Rating value={product.rating} count={product.reviews} />
          <div className="price">{formatPrice(product.price)}</div>
          <p className="desc">{product.description}</p>
          <div className="stock">{product.stock < 10 ? <Badge>仅剩 {product.stock} 件</Badge> : `库存 ${product.stock} 件`}</div>
          <div><Button size="lg" onClick={addToCart}>加入购物车</Button></div>
        </div>
      </div>
      {reco.length > 0 && (
        <section className="reco">
          <h2>猜你喜欢</h2>
          <div className="grid">
            {reco.map((p) => (
              <Link key={p.id} to={`/p/${p.slug}`} className="card">
                <Pic name={p.slug} alt={p.name} sizes="(max-width: 720px) 50vw, 240px" loading="lazy" />
                <h3>{p.name}</h3>
                <div className="p">{formatPrice(p.price)}</div>
              </Link>
            ))}
          </div>
        </section>
      )}
      <section className="reviews">
        <h2>评价{reviews && `（${reviews.length}）`}</h2>
        {!reviews ? <p className="empty">加载中…</p> : reviews.map((r, i) => (
          <div className="review" key={i}>
            <div className="who"><span>{r.user} · <Rating value={r.rating} /></span><span title={formatDateTime(r.date)}>{relativeTime(r.date)}</span></div>
            <div>{r.text}</div>
          </div>
        ))}
      </section>
    </>
  );
}
